import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  useRemoveCourseMutation,
  useUpdateCourseStatusForAdminMutation,
} from '@teachur/http-client';
import type { UserCourse } from '@teachur/http-client';

type CourseAction = 'delete' | 'start' | 'stop';

interface ActionTarget {
  action: CourseAction;
  courses: UserCourse[];
}

interface UseCourseActionsProps {
  selectedCourses: UserCourse[];
  refetchCourses: () => Promise<unknown>;
  clearSelection: () => void;
}

export const useCourseActions = ({
  selectedCourses,
  refetchCourses,
  clearSelection,
}: UseCourseActionsProps) => {
  const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null);
  const [isPending, setIsPending] = useState(false);

  const removeCourseMutation = useRemoveCourseMutation({
    onError: () => toast.error('Failed to remove course'),
  });

  const updateCourseStatusMutation = useUpdateCourseStatusForAdminMutation();

  const openSingleDelete = (course: UserCourse) => {
    setActionTarget({ action: 'delete', courses: [course] });
  };

  const openSingleStart = (course: UserCourse) => {
    setActionTarget({ action: 'start', courses: [course] });
  };

  const openSingleStop = (course: UserCourse) => {
    setActionTarget({ action: 'stop', courses: [course] });
  };

  const openBulkDelete = () => {
    const deletableCourses = selectedCourses.filter((course) => !course.passed && !course.started);

    if (!deletableCourses.length) {
      toast.warning('No removable courses selected (only non-started courses can be removed)');
      return;
    }

    setActionTarget({ action: 'delete', courses: deletableCourses });
  };

  const openBulkStart = () => {
    const startableCourses = selectedCourses.filter((course) => !course.passed && !course.started);

    if (!startableCourses.length) {
      toast.warning('No courses to start in selection');
      return;
    }

    setActionTarget({ action: 'start', courses: startableCourses });
  };

  const openBulkStop = () => {
    const stoppableCourses = selectedCourses.filter((course) => !course.passed && course.started);

    if (!stoppableCourses.length) {
      toast.warning('No running courses in selection');
      return;
    }

    setActionTarget({ action: 'stop', courses: stoppableCourses });
  };

  const closeActionModal = () => {
    setActionTarget(null);
  };

  const confirmAction = async () => {
    if (!actionTarget) return;

    setIsPending(true);

    try {
      if (actionTarget.action === 'delete') {
        await Promise.all(
          actionTarget.courses.map((course) =>
            removeCourseMutation.mutateAsync({
              courseId: course.courseId,
              userId: course.userId,
            })
          )
        );

        toast.success(
          actionTarget.courses.length > 1
            ? `${actionTarget.courses.length} courses removed`
            : 'Course removed'
        );
      } else {
        const status = actionTarget.action === 'start' ? 'started' : 'initial';

        await Promise.all(
          actionTarget.courses.map((course) =>
            updateCourseStatusMutation.mutateAsync({
              userId: course.userId,
              courseId: course.courseId,
              status,
            })
          )
        );

        toast.success(
          actionTarget.courses.length > 1
            ? `${actionTarget.courses.length} courses updated`
            : 'Course status updated'
        );
      }

      setActionTarget(null);
      clearSelection();
      await refetchCourses();
    } catch {
      toast.error(
        actionTarget.action === 'delete'
          ? 'Failed to remove courses'
          : 'Failed to update course status'
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    actionTarget,
    isPending,
    openSingleDelete,
    openSingleStart,
    openSingleStop,
    openBulkDelete,
    openBulkStart,
    openBulkStop,
    closeActionModal,
    confirmAction,
  };
};
